import { TranslationStringType } from "@localization/LocalizationContext";
import { RiskManagementReportViewModel, RiskReportCoverage } from "./RiskManagementReport.types";

const targetTickCount = 5;

// Rounds a raw step up to a "nice" integer (1, 2, 5, 10, 20, 50, …) so a count axis reads cleanly at
// any scale — a step of 1 when only a handful of items exist, a coarser step when there are many.
function niceStep(rawStep: number) {
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalized = rawStep / magnitude;
    const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return niceNormalized * magnitude;
}

export function buildCountAxisTicks(highest: number) {
    const step = niceStep(Math.max(1, highest / targetTickCount));
    const axisMax = Math.ceil(highest / step) * step;
    const tickValues = Array.from({ length: Math.floor(axisMax / step) + 1 }, (_, index) => index * step);
    return { axisMax, tickValues };
}

// What the coverage card counts follows the levels the customer actually assesses on, because the two levels
// have different row units: an entity carries many scenarios, an organisation scenario is one risk on its own.
// Three whole wordings rather than one sentence with the noun swapped in, so each language keeps its own
// grammar. A customer with no organisation scenarios reads exactly what it read before this existed.
export function buildRiskReportCoverage(data: RiskManagementReportViewModel | undefined, translateString: TranslationStringType): RiskReportCoverage {
    const assessedEntities = data?.assessedEntities ?? 0;
    const totalEntities = data?.totalEntities ?? 0;
    const assessedScenarios = data?.assessedOrganisationScenarios ?? 0;
    const totalScenarios = data?.totalOrganisationScenarios ?? 0;

    if (totalScenarios === 0) {
        return {
            assessed: assessedEntities,
            total: totalEntities,
            header: translateString("riskManagementReportAssessedEntities"),
            label: translateString("ofEntitiesCount", { count: totalEntities }),
        };
    }

    if (totalEntities === 0) {
        return {
            assessed: assessedScenarios,
            total: totalScenarios,
            header: translateString("riskManagementReportAssessedScenarios"),
            label: translateString("ofScenariosCount", { count: totalScenarios }),
        };
    }

    // Both levels are in use, so the headline pair names neither unit and the two fractions follow on their own
    // line: a merged fraction alone would hide which level the gap sits in, while putting the two fractions where
    // the label goes would leave the big number reading as part of the first one.
    // The line is composed from the two per-level phrases rather than written as one sentence with four numbers
    // in it, so each half keeps its own plural form: "1 of 1 scenario", not "1 of 1 scenarios".
    const entityFraction = `${assessedEntities} ${translateString("ofEntitiesCount", { count: totalEntities })}`;
    const scenarioFraction = `${assessedScenarios} ${translateString("ofScenariosCount", { count: totalScenarios })}`;

    return {
        assessed: assessedEntities + assessedScenarios,
        total: totalEntities + totalScenarios,
        header: translateString("riskManagementReportAssessedRisks"),
        label: translateString("ofRisksCount", { count: totalEntities + totalScenarios }),
        breakdown: `${entityFraction}, ${scenarioFraction}`,
    };
}
